import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  FaFilePdf, 
  FaFileWord, 
  FaFileExcel, 
  FaFilePowerpoint, 
  FaFileVideo, 
  FaFileAudio, 
  FaFileAlt, 
  FaFileArchive, 
  FaCode, 
  FaFile,
  FaImage
} from 'react-icons/fa';
import './TaskModal.css';
import api from '../../api';
import RichTextEditor from './RichTextEditor';
import { useAuth } from '../../contexts/AuthContext';

export default function TaskModal({ task, onClose, onUpdate, onDelete, canEdit = true, onSubtaskChange }) {
  const { user } = useAuth();
  const [editedTask, setEditedTask] = useState(task);
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]); // Available task types
  const [priorities, setPriorities] = useState([]); // Available priorities
  const [statuses, setStatuses] = useState([]); // Available statuses
  const [people, setPeople] = useState([]); // Available people/requesters
  const [files, setFiles] = useState([]); // Task files (all types)
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // For file preview modal
  const [assignees, setAssignees] = useState([]); // Task assignees
  const [editingAssignee, setEditingAssignee] = useState(null); // ID of assignee being edited
  const [editAssigneeHours, setEditAssigneeHours] = useState('');
  const [editAssigneePoints, setEditAssigneePoints] = useState('');
  const [newAssigneePersonId, setNewAssigneePersonId] = useState('');
  const [newAssigneeHours, setNewAssigneeHours] = useState('');
  const [newAssigneePoints, setNewAssigneePoints] = useState('');
  const [sprints, setSprints] = useState([]); // Available sprints for the task's board
  const [currentSprint, setCurrentSprint] = useState(null); // Current sprint assignment
  const [boards, setBoards] = useState([]); // Available boards (to get board_id from team_id)
  const [teams, setTeams] = useState([]); // Available teams
  const [boardStages, setBoardStages] = useState([]); // Board-specific stages
  const [subtasks, setSubtasks] = useState([]); // Task subtasks
  const [editingSubtask, setEditingSubtask] = useState(null); // ID of subtask being edited
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [newSubtaskDescription, setNewSubtaskDescription] = useState('');
  const [newSubtaskStatusId, setNewSubtaskStatusId] = useState('');
  const [newSubtaskTypeId, setNewSubtaskTypeId] = useState('');
  const [editingSubtaskAssignee, setEditingSubtaskAssignee] = useState(null); // ID of subtask assignee being edited
  const [editSubtaskAssigneeHours, setEditSubtaskAssigneeHours] = useState('');
  const [editSubtaskAssigneePoints, setEditSubtaskAssigneePoints] = useState('');
  const [newSubtaskAssigneePersonId, setNewSubtaskAssigneePersonId] = useState({}); // Map of subtaskId -> personId
  const [newSubtaskAssigneeHours, setNewSubtaskAssigneeHours] = useState({}); // Map of subtaskId -> hours
  const [newSubtaskAssigneePoints, setNewSubtaskAssigneePoints] = useState({}); // Map of subtaskId -> points
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set()); // Set of expanded subtask IDs
  const [showNewSubtaskForm, setShowNewSubtaskForm] = useState(false); // Whether to show the new subtask form
  const contentRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch all dropdown options on mount (except types - those are board-specific)
  useEffect(() => {
    Promise.all([
      api.get('/tasks/priorities'),
      api.get('/tasks/statuses'),
      api.get('/tasks/people'),
      api.get('/tasks/teams'),
      api.get('/boards')
    ])
      .then(([prioritiesRes, statusesRes, peopleRes, teamsRes, boardsRes]) => {
        setPriorities(prioritiesRes.data || []);
        setStatuses(statusesRes.data || []);
        setPeople(peopleRes.data || []);
        setTeams(teamsRes.data || []);
        setBoards(boardsRes.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch dropdown options:", err);
      });
  }, []);

  // Fetch board-specific task types and stages when task's team/board changes
  useEffect(() => {
    if (editedTask?.team_id && boards.length > 0) {
      const board = boards.find(b => b.team_id === editedTask.team_id);
      if (board) {
        fetchBoardTaskTypes(board.id);
        fetchBoardStages(board.id);
      } else {
        setTaskTypes([]);
        setBoardStages([]);
      }
    } else {
      setTaskTypes([]);
      setBoardStages([]);
    }
  }, [editedTask?.team_id, boards]);

  const fetchBoardStages = async (boardId) => {
    if (!boardId) {
      setBoardStages([]);
      return;
    }
    try {
      const res = await api.get(`/board-settings/${boardId}/stages`);
      setBoardStages(res.data || []);
    } catch (err) {
      console.error("Failed to fetch board stages:", err);
      setBoardStages([]);
    }
  };

  const fetchBoardTaskTypes = async (boardId) => {
    if (!boardId) {
      setTaskTypes([]);
      return;
    }
    try {
      const res = await api.get(`/board-settings/${boardId}/types`);
      setTaskTypes(res.data || []);
    } catch (err) {
      console.error("Failed to fetch board task types:", err);
      setTaskTypes([]);
    }
  };

  useEffect(() => {
    setEditedTask(task);
    // Load comments, files, assignees, sprint, and subtasks for this task in parallel for faster loading
    if (task?.id) {
      Promise.all([
        loadComments(task.id),
        loadFiles(task.id),
        loadAssignees(task.id),
        loadTaskSprint(task.id),
        loadSubtasks(task.id)
      ]).catch(err => {
        console.error('Error loading task data:', err);
      });
    }
  }, [task]);

  // Load sprints when task's team/board changes
  useEffect(() => {
    if (editedTask?.team_id) {
      loadSprintsForBoard(editedTask.team_id);
    }
  }, [editedTask?.team_id, boards]);

  // Check if sprint is still valid when team/board changes
  useEffect(() => {
    if (editedTask?.team_id && currentSprint && boards.length > 0) {
      const board = boards.find(b => b.team_id === editedTask.team_id);
      if (board && currentSprint.board_id !== board.id) {
        // Task moved to different board - remove from sprint
        const sprintName = currentSprint.name;
        handleSprintChange('').then(() => {
          alert(`Task moved to different board. Removed from sprint "${sprintName}".`);
        }).catch(() => {
          // Error already handled in handleSprintChange
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedTask?.team_id, currentSprint?.id, boards]);

  // Handle ESC key to close preview
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && previewFile) {
        setPreviewFile(null);
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [previewFile]);




  // Inject critical CSS into document head IMMEDIATELY (before any rendering)
  // This must run synchronously to prevent CSS loading order issues
  useLayoutEffect(() => {
    if (!task) return;

    // Inject CSS into head if not already there
    // Insert at the START of head to ensure it loads first
    let styleElement = document.getElementById('task-modal-critical-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'task-modal-critical-styles';
      styleElement.textContent = `
        #task-modal-content-wrapper {
          position: fixed !important;
          top: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 50% !important;
          min-width: 600px !important;
          max-width: 800px !important;
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
            #task-modal-content-element,
            .task-modal-content {
              position: absolute !important;
              top: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              left: 0 !important;
              width: 100% !important;
              height: 100% !important;
              margin: 0 !important;
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              text-rendering: optimizeLegibility !important;
            }
      `;
      // Insert at the START of head (before other stylesheets) for highest priority
      document.head.insertBefore(styleElement, document.head.firstChild);
    }
  }, [task]);

      // Use useLayoutEffect to trigger slide-in animation
      useLayoutEffect(() => {
        if (!task || !contentRef.current) {
          setIsReady(false);
          setIsPositioned(false);
          return;
        }

        const element = contentRef.current;
        
        // Trigger slide-in animation from right
        requestAnimationFrame(() => {
          void element.offsetHeight; // Force reflow
          setIsPositioned(true);
          setIsReady(true);
        });
      }, [task]);

  const loadComments = async (taskId) => {
    try {
      const res = await api.get(`/tasks/${taskId}/comments`);
      setComments(res.data || []);
      return res.data || [];
    } catch (err) {
      console.error('Failed to load comments:', err);
      setComments([]);
      return [];
    }
  };

  const loadFiles = async (taskId) => {
    try {
      const res = await api.get(`/task-images/task/${taskId}`);
      setFiles(res.data || []);
      return res.data || [];
    } catch (err) {
      console.error('Failed to load files:', err);
      setFiles([]);
      return [];
    }
  };

  const loadAssignees = async (taskId) => {
    try {
      const res = await api.get(`/assignees/task/${taskId}`);
      setAssignees(res.data || []);
      return res.data || [];
    } catch (err) {
      console.error('Failed to load assignees:', err);
      setAssignees([]);
      return [];
    }
  };

  const loadSubtasks = async (taskId) => {
    try {
      const res = await api.get(`/subtasks/task/${taskId}`);
      setSubtasks(res.data || []);
      return res.data || [];
    } catch (err) {
      console.error('Failed to load subtasks:', err);
      setSubtasks([]);
      return [];
    }
  };

  const handleAddAssignee = async (e) => {
    e.preventDefault();
    if (!newAssigneePersonId || !editedTask?.id) return;

    try {
      await api.post('/assignees', {
        task_id: editedTask.id,
        person_id: parseInt(newAssigneePersonId),
        assigned_hours: newAssigneeHours ? parseFloat(newAssigneeHours) : null,
        assigned_points: newAssigneePoints ? parseInt(newAssigneePoints) : null
      });
      
      // Reload assignees
      await loadAssignees(editedTask.id);
      
      // Reset form
      setNewAssigneePersonId('');
      setNewAssigneeHours('');
      setNewAssigneePoints('');
    } catch (err) {
      console.error('Failed to add assignee:', err);
      alert('Failed to add assignee. Please try again.');
    }
  };

  const handleEditAssignee = (assignee) => {
    setEditingAssignee(assignee.id);
    setEditAssigneeHours(assignee.assigned_hours || '');
    setEditAssigneePoints(assignee.assigned_points || '');
  };

  const handleCancelEditAssignee = () => {
    setEditingAssignee(null);
    setEditAssigneeHours('');
    setEditAssigneePoints('');
  };

  const handleUpdateAssignee = async (assigneeId) => {
    try {
      await api.put(`/assignees/${assigneeId}`, {
        assigned_hours: editAssigneeHours ? parseFloat(editAssigneeHours) : null,
        assigned_points: editAssigneePoints ? parseInt(editAssigneePoints) : null
      });
      
      // Reload assignees to get updated data
      await loadAssignees(editedTask.id);
      setEditingAssignee(null);
      setEditAssigneeHours('');
      setEditAssigneePoints('');
    } catch (err) {
      console.error('Failed to update assignee:', err);
      alert('Failed to update assignee. Please try again.');
    }
  };

  const handleRemoveAssignee = async (assigneeId) => {
    if (!window.confirm('Remove this assignee?')) return;

    try {
      await api.delete(`/assignees/${assigneeId}`);
      setAssignees(prev => prev.filter(a => a.id !== assigneeId));
    } catch (err) {
      console.error('Failed to remove assignee:', err);
      alert('Failed to remove assignee. Please try again.');
    }
  };

  // Subtask management functions
  const handleCreateSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtaskTitle || !editedTask?.id) return;

    try {
      await api.post('/subtasks', {
        task_id: editedTask.id,
        title: newSubtaskTitle,
        description: newSubtaskDescription || null,
        statusId: newSubtaskStatusId ? parseInt(newSubtaskStatusId) : null,
        typeId: newSubtaskTypeId ? parseInt(newSubtaskTypeId) : null
      });
      
      // Reload subtasks
      await loadSubtasks(editedTask.id);
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
      
      // Reset form
      setNewSubtaskTitle('');
      setNewSubtaskDescription('');
      setNewSubtaskStatusId('');
      setNewSubtaskTypeId('');
      setShowNewSubtaskForm(false);
    } catch (err) {
      console.error('Failed to create subtask:', err);
      alert('Failed to create subtask. Please try again.');
    }
  };

  const handleUpdateSubtask = async (subtaskId, updates) => {
    try {
      await api.put(`/subtasks/${subtaskId}`, updates);
      await loadSubtasks(editedTask.id);
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
      
      setEditingSubtask(null);
    } catch (err) {
      console.error('Failed to update subtask:', err);
      alert('Failed to update subtask. Please try again.');
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!window.confirm('Delete this subtask?')) return;

    try {
      await api.delete(`/subtasks/${subtaskId}`);
      setSubtasks(prev => prev.filter(s => s.id !== subtaskId));
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
    } catch (err) {
      console.error('Failed to delete subtask:', err);
      alert('Failed to delete subtask. Please try again.');
    }
  };

  // Subtask assignee management functions
  const loadSubtaskAssignees = async (subtaskId) => {
    try {
      const res = await api.get(`/subtasks/${subtaskId}/assignees`);
      // Update the subtask in the subtasks array with its assignees
      setSubtasks(prev => prev.map(s => 
        s.id === subtaskId ? { ...s, assignees: res.data || [] } : s
      ));
      return res.data || [];
    } catch (err) {
      console.error('Failed to load subtask assignees:', err);
      return [];
    }
  };

  const handleAddSubtaskAssignee = async (e, subtaskId) => {
    e.preventDefault();
    const personId = newSubtaskAssigneePersonId[subtaskId];
    const hours = newSubtaskAssigneeHours[subtaskId];
    const points = newSubtaskAssigneePoints[subtaskId];
    
    if (!personId || !editedTask?.id) return;

    try {
      await api.post(`/subtasks/${subtaskId}/assignees`, {
        person_id: parseInt(personId),
        assigned_hours: hours ? parseFloat(hours) : null,
        assigned_points: points ? parseInt(points) : null
      });
      
      // Reload assignees for this subtask
      await loadSubtaskAssignees(subtaskId);
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
      
      // Reset form for this subtask
      setNewSubtaskAssigneePersonId(prev => {
        const updated = { ...prev };
        delete updated[subtaskId];
        return updated;
      });
      setNewSubtaskAssigneeHours(prev => {
        const updated = { ...prev };
        delete updated[subtaskId];
        return updated;
      });
      setNewSubtaskAssigneePoints(prev => {
        const updated = { ...prev };
        delete updated[subtaskId];
        return updated;
      });
    } catch (err) {
      console.error('Failed to add subtask assignee:', err);
      alert('Failed to add assignee. Please try again.');
    }
  };

  const handleEditSubtaskAssignee = (subtaskId, assignee) => {
    setEditingSubtaskAssignee(`${subtaskId}-${assignee.id}`);
    setEditSubtaskAssigneeHours(assignee.assigned_hours || '');
    setEditSubtaskAssigneePoints(assignee.assigned_points || '');
  };

  const handleCancelEditSubtaskAssignee = () => {
    setEditingSubtaskAssignee(null);
    setEditSubtaskAssigneeHours('');
    setEditSubtaskAssigneePoints('');
  };

  const handleUpdateSubtaskAssignee = async (subtaskId, assigneeId) => {
    try {
      await api.put(`/subtasks/${subtaskId}/assignees/${assigneeId}`, {
        assigned_hours: editSubtaskAssigneeHours ? parseFloat(editSubtaskAssigneeHours) : null,
        assigned_points: editSubtaskAssigneePoints ? parseInt(editSubtaskAssigneePoints) : null
      });
      
      // Reload assignees for this subtask
      await loadSubtaskAssignees(subtaskId);
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
      
      setEditingSubtaskAssignee(null);
      setEditSubtaskAssigneeHours('');
      setEditSubtaskAssigneePoints('');
    } catch (err) {
      console.error('Failed to update subtask assignee:', err);
      alert('Failed to update assignee. Please try again.');
    }
  };

  const handleRemoveSubtaskAssignee = async (subtaskId, assigneeId) => {
    if (!window.confirm('Remove this assignee from the subtask?')) return;

    try {
      await api.delete(`/subtasks/${subtaskId}/assignees/${assigneeId}`);
      await loadSubtaskAssignees(subtaskId);
      
      // Notify parent that subtasks changed (for burndown chart refresh)
      if (onSubtaskChange) {
        onSubtaskChange();
      }
    } catch (err) {
      console.error('Failed to remove subtask assignee:', err);
      alert('Failed to remove assignee. Please try again.');
    }
  };

  const handleSubtaskFieldUpdate = async (subtaskId, field, value) => {
    try {
      const updates = {};
      if (field === 'statusId') {
        updates.statusId = value ? parseInt(value) : null;
      } else if (field === 'typeId') {
        updates.typeId = value ? parseInt(value) : null;
      } else if (field === 'title') {
        updates.title = value;
      } else if (field === 'description') {
        updates.description = value || null;
      }
      
      await handleUpdateSubtask(subtaskId, updates);
    } catch (err) {
      console.error('Failed to update subtask field:', err);
      alert('Failed to update subtask. Please try again.');
    }
  };

  const loadSprintsForBoard = async (teamId) => {
    try {
      // Find board for this team
      const board = boards.find(b => b.team_id === teamId);
      if (!board) {
        setSprints([]);
        return;
      }

      const res = await api.get(`/sprints/board/${board.id}`);
      const sprintsList = res.data || [];
      setSprints(sprintsList);
      
      // If task has a sprint, verify it's still valid
      if (currentSprint) {
        const sprintStillValid = sprintsList.some(s => s.id === currentSprint.id);
        if (!sprintStillValid) {
          // Sprint no longer exists or is on different board
          setCurrentSprint(null);
        }
      }
    } catch (err) {
      console.error('Failed to load sprints:', err);
      setSprints([]);
    }
  };

  const loadTaskSprint = async (taskId) => {
    try {
      const res = await api.get(`/sprints/task/${taskId}`);
      setCurrentSprint(res.data || null);
    } catch (err) {
      console.error('Failed to load task sprint:', err);
      setCurrentSprint(null);
    }
  };

  const handleSprintChange = async (sprintId) => {
    if (!editedTask?.id || !canEdit) return;

    // Optimistically update the UI immediately
    const previousSprintId = currentSprint?.id;
    const selectedSprint = sprintId && sprintId !== '' 
      ? sprints.find(s => s.id === parseInt(sprintId)) 
      : null;
    setCurrentSprint(selectedSprint || null);

    try {
      // Remove from previous sprint if exists
      if (previousSprintId) {
        try {
          await api.delete(`/sprints/${previousSprintId}/tasks/${editedTask.id}`);
        } catch (err) {
          // Ignore if not in sprint
        }
      }

      // Add to new sprint if selected
      if (sprintId && sprintId !== '') {
        await api.post(`/sprints/${sprintId}/tasks`, {
          task_id: editedTask.id,
          estimated_hours: null,
          estimated_points: null
        });
      }
    } catch (err) {
      console.error('Failed to update sprint assignment:', err);
      // Revert optimistic update on error
      await loadTaskSprint(editedTask.id);
      alert('Failed to update sprint assignment. Please try again.');
    }
  };

  // Reload sprint when sprints list changes (in case sprint was just created)
  useEffect(() => {
    if (editedTask?.id && sprints.length > 0 && !currentSprint) {
      // Check if task should be in a sprint
      loadTaskSprint(editedTask.id);
    }
  }, [sprints]);

  const handleFileUpload = async (fileList) => {
    const filesToUpload = Array.from(fileList || []);
    console.log('handleFileUpload called with:', filesToUpload.length, 'files, task ID:', editedTask?.id);
    if (filesToUpload.length === 0 || !editedTask?.id) {
      console.log('Skipping upload - no files or no task ID');
      return;
    }

    setUploadingFiles(true);
    try {
      const uploadPromises = filesToUpload.map(async (file) => {
        // Upload to blob storage
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await api.post('/images/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Associate file with task
        const fileRes = await api.post('/task-images', {
          task_id: editedTask.id,
          file_url: uploadRes.data.fileUrl || uploadRes.data.imageUrl,
          image_url: uploadRes.data.fileUrl || uploadRes.data.imageUrl, // Backward compatibility
          file_name: uploadRes.data.fileName, // Unique filename for blob storage
          original_file_name: uploadRes.data.originalFileName || uploadRes.data.fileName, // Original filename for display
          file_size: uploadRes.data.fileSize,
          mime_type: uploadRes.data.mimeType,
        });

        return fileRes.data;
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setFiles([...files, ...uploadedFiles]);
    } catch (err) {
      console.error('Failed to upload files:', err);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
      // Input is already reset in handleFileInputChange
    }
  };

  const handleFileInputChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      console.log('No files selected');
      return;
    }
    console.log('Files selected:', files.length);
    // Don't reset input until after upload completes
    await handleFileUpload(files);
    // Reset input after upload to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Trigger click immediately without delay
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    // Optimistic UI update - remove immediately
    setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    
    try {
      // Delete from database
      await api.delete(`/task-images/${fileId}`);
      
      // Delete from blob storage
      await api.delete(`/images/${fileName}`);
    } catch (err) {
      console.error('Failed to delete file:', err);
      // Revert on error - reload files
      if (editedTask?.id) {
        loadFiles(editedTask.id);
      }
      alert('Failed to delete file. Please try again.');
    }
  };

  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  const isVideoFile = (mimeType, fileName) => {
    if (mimeType?.startsWith('video/')) return true;
    if (fileName?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/)) return true;
    return false;
  };

  const getFileIcon = (mimeType, fileName) => {
    if (isImageFile(mimeType)) return <FaImage />;
    
    // PDF
    if (mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) {
      return <FaFilePdf style={{ color: '#DC143C' }} />;
    }
    
    // Word documents
    if (mimeType?.includes('word') || mimeType?.includes('msword') || 
        fileName?.toLowerCase().endsWith('.doc') || fileName?.toLowerCase().endsWith('.docx')) {
      return <FaFileWord style={{ color: '#2B579A' }} />;
    }
    
    // Excel
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') ||
        fileName?.toLowerCase().endsWith('.xls') || fileName?.toLowerCase().endsWith('.xlsx') ||
        fileName?.toLowerCase().endsWith('.csv')) {
      return <FaFileExcel style={{ color: '#1D6F42' }} />;
    }
    
    // PowerPoint
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation') ||
        fileName?.toLowerCase().endsWith('.ppt') || fileName?.toLowerCase().endsWith('.pptx')) {
      return <FaFilePowerpoint style={{ color: '#D04423' }} />;
    }
    
    // Video files
    if (mimeType?.startsWith('video/') || 
        fileName?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/)) {
      return <FaFileVideo style={{ color: '#FF6B6B' }} />;
    }
    
    // Audio files
    if (mimeType?.startsWith('audio/') ||
        fileName?.toLowerCase().match(/\.(mp3|wav|flac|aac|ogg|m4a)$/)) {
      return <FaFileAudio style={{ color: '#4ECDC4' }} />;
    }
    
    // Text files
    if (mimeType?.includes('text/') || 
        fileName?.toLowerCase().match(/\.(txt|md|rtf)$/)) {
      return <FaFileAlt style={{ color: '#95A5A6' }} />;
    }
    
    // Zip/Archive files
    if (mimeType?.includes('zip') || mimeType?.includes('archive') ||
        fileName?.toLowerCase().match(/\.(zip|rar|7z|tar|gz)$/)) {
      return <FaFileArchive style={{ color: '#F39C12' }} />;
    }
    
    // Code files
    if (fileName?.toLowerCase().match(/\.(js|ts|jsx|tsx|html|css|json|xml|py|java|cpp|c|h)$/)) {
      return <FaCode style={{ color: '#3498DB' }} />;
    }
    
    // Default
    return <FaFile style={{ color: '#7F8C8D' }} />;
  };

  // Auto-save with debouncing
  useEffect(() => {
    if (!editedTask || !task) return;
    
    // Don't save on initial load
    if (JSON.stringify(editedTask) === JSON.stringify(task)) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setIsSaving(true);
    
    // Debounce save by 1 second
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const payload = {
          title: editedTask.title,
          description: editedTask.description,
          type: editedTask.type || null,
          priority: editedTask.priority || null,
          status: editedTask.status || null,
          personName: editedTask.personName || editedTask.requester || null,
          personEmail: editedTask.personEmail || editedTask.email || null,
          team_id: editedTask.team_id || null,
        };

        const res = await api.put(`/tasks/${editedTask.id}`, payload);
        if (onUpdate) {
          onUpdate(res.data);
        }
        setIsSaving(false);
      } catch (err) {
        console.error('Failed to save task:', err);
        setIsSaving(false);
        alert('Failed to save changes. Please try again.');
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [editedTask, task, onUpdate]);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for slide-out animation before closing
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${editedTask.title}"?`)) {
      try {
        await api.delete(`/tasks/${editedTask.id}`);
        if (onDelete) {
          onDelete(editedTask.id);
        }
        handleClose();
      } catch (err) {
        console.error('Failed to delete task:', err);
        alert('Failed to delete task. Please try again.');
      }
    }
  };

  const handleAddComment = async () => {
    // Strip HTML tags to check if there's actual content
    const textContent = newComment ? newComment.replace(/<[^>]*>/g, '').trim() : '';
    if (!textContent || !editedTask?.id) return;
    
    try {
      // Get personId from the logged-in user (person who made the comment)
      // Require authentication to comment
      if (!user) {
        alert('You must be logged in to add comments.');
        return;
      }
      
      const personId = user.id;
      const personEmail = user.email;
      
      const res = await api.post(`/tasks/${editedTask.id}/comments`, {
        text: newComment, // Send HTML content
        personId: personId,
        personEmail: personEmail
      });
      
      setComments([res.data, ...comments]);
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || 'Unknown error';
      alert(`Failed to add comment: ${errorMessage}`);
    }
  };

  if (!task) return null;

      const modalContent = (
    <div className="task-modal-overlay" ref={overlayRef} onClick={handleClose}>
        <div 
          id="task-modal-content-wrapper"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '50%',
            minWidth: '600px',
            maxWidth: '800px',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 2001,
            margin: 0,
            padding: 0
          }}
        >
          <div 
            id="task-modal-content-element"
            ref={contentRef}
            className={`task-modal-content ${isReady ? 'ready' : ''}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              margin: 0,
              transform: (isPositioned && !isClosing) ? 'translateX(0)' : 'translateX(100%)',
              pointerEvents: 'auto',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
        <div className="task-modal-header">
          <button className="task-modal-close" onClick={handleClose}>×</button>
          {isSaving && <span className="task-modal-saving">Saving...</span>}
        </div>
        
        <input
          className="task-modal-title-input"
          value={editedTask.title || ''}
          onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
          placeholder="Task title"
          disabled={!canEdit}
        />
        
        <div className="task-modal-details">
          <div className="task-modal-field">
            <label>Description</label>
            <RichTextEditor
              value={editedTask.description || ''}
              onChange={(value) => setEditedTask({ ...editedTask, description: value })}
              placeholder="No description provided"
              onFileAttach={canEdit ? handleFileUpload : undefined}
              taskId={editedTask?.id}
              readOnly={!canEdit}
            />
          </div>

          {/* Files Section - moved below description */}
          <div className="task-modal-files">
            <label className="task-modal-files-label">Files</label>
            {canEdit && (
            <div className="task-modal-files-upload">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', zIndex: -1 }}
                id="file-upload-input"
                tabIndex={-1}
              />
              <button 
                type="button"
                onClick={handleUploadButtonClick}
                className="task-modal-upload-btn"
                disabled={uploadingFiles}
              >
                {uploadingFiles ? 'Uploading...' : '+ Upload File'}
              </button>
            </div>
            )}
            
            {files.length > 0 && (
              <div className="task-modal-files-list">
                {files.map((file) => (
                  <div key={file.id} className="task-modal-file-item">
                    <div className="task-modal-file-wrapper">
                      {isImageFile(file.mime_type) ? (
                        <>
                          <img
                            src={file.image_url}
                            alt={file.file_name}
                            className="task-modal-file-thumbnail"
                            onClick={() => setPreviewFile(file)}
                            loading="lazy"
                          />
                          <div className="task-modal-image-filename-overlay">
                            <span className="file-name">{file.original_file_name || file.file_name}</span>
                          </div>
                          {canEdit && (
                          <button
                            className="task-modal-file-delete"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const displayName = file.original_file_name || file.file_name;
                              if (window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
                                await handleDeleteFile(file.id, file.file_name);
                              }
                            }}
                            title="Delete image"
                          >
                            ×
                          </button>
                          )}
                        </>
                      ) : isVideoFile(file.mime_type, file.file_name) ? (
                        <>
                          <div 
                            className="task-modal-file-icon"
                            onClick={() => setPreviewFile(file)}
                            title="Click to preview video"
                          >
                            <span className="file-icon-emoji">{getFileIcon(file.mime_type, file.original_file_name || file.file_name)}</span>
                            <span className="file-name">{file.original_file_name || file.file_name}</span>
                          </div>
                          <button
                            className="task-modal-file-delete"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const displayName = file.original_file_name || file.file_name;
                              if (window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
                                await handleDeleteFile(file.id, file.file_name);
                              }
                            }}
                            title="Delete video"
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <>
                          <div 
                            className="task-modal-file-icon"
                            onClick={() => window.open(file.image_url, '_blank')}
                            title="Click to open file"
                          >
                            <span className="file-icon-emoji">{getFileIcon(file.mime_type, file.original_file_name || file.file_name)}</span>
                            <span className="file-name">{file.original_file_name || file.file_name}</span>
                          </div>
                          <button
                            className="task-modal-file-delete"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const displayName = file.original_file_name || file.file_name;
                              if (window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
                                await handleDeleteFile(file.id, file.file_name);
                              }
                            }}
                            title="Delete file"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="task-modal-row">
            <div className="task-modal-field">
              <label>{boardStages.length > 0 ? 'Stage' : 'Status'}</label>
              {boardStages.length > 0 ? (
                <select
                  className="task-modal-select"
                  value={(() => {
                    // Find stage that maps to current task status
                    const stage = boardStages.find(s => s.status === editedTask.status);
                    return stage ? stage.id : '';
                  })()}
                  onChange={(e) => {
                    const stageId = e.target.value;
                    const stage = boardStages.find(s => s.id === parseInt(stageId, 10));
                    setEditedTask({ ...editedTask, status: stage?.status || null });
                  }}
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {boardStages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.stage_name}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  className="task-modal-select"
                  value={editedTask.status || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                  disabled={!canEdit}
                >
                  <option value="">-</option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.status}>
                      {status.status}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="task-modal-field">
              <label>Type</label>
              <select
                className="task-modal-select"
                value={editedTask.type || ''}
                onChange={(e) => setEditedTask({ ...editedTask, type: e.target.value })}
                disabled={!canEdit}
              >
                <option value="">-</option>
                {[...taskTypes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((type) => (
                  <option key={type.id} value={type.type}>
                    {type.type}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="task-modal-row">
            {/* Sprint Assignment - in same row as Priority */}
            {editedTask?.team_id && (
              <div className="task-modal-field">
                <label>Sprint</label>
                <select
                  className="task-modal-select"
                  value={currentSprint?.id || ''}
                  onChange={(e) => handleSprintChange(e.target.value)}
                  disabled={!canEdit || sprints.length === 0}
                >
                  <option value="">No Sprint</option>
                  {sprints.map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprint.name} {sprint.is_active && '(Active)'}
                    </option>
                  ))}
                </select>
                {sprints.length === 0 && editedTask?.team_id && (
                  <span style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.25rem', display: 'block' }}>
                    No sprints available for this board
                  </span>
                )}
              </div>
            )}
            
            <div className="task-modal-field">
              <label>Priority</label>
              <select
                className="task-modal-select"
                value={editedTask.priority || ''}
                onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                disabled={!canEdit}
              >
                <option value="">-</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.priorityLevel}>
                    {priority.priorityLevel}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="task-modal-row">
            <div className="task-modal-field">
              <label>Requester</label>
              <select
                className="task-modal-select"
                value={editedTask.personName || editedTask.requester || ''}
                onChange={(e) => {
                  const selectedPerson = people.find(p => p.name === e.target.value);
                  setEditedTask({ 
                    ...editedTask, 
                    personName: e.target.value, 
                    requester: e.target.value,
                    personEmail: selectedPerson?.emailAddress || editedTask.personEmail || editedTask.email,
                    email: selectedPerson?.emailAddress || editedTask.personEmail || editedTask.email
                  });
                }}
                disabled={!canEdit}
              >
                <option value="">-</option>
                {people.map((person) => (
                  <option key={person.id} value={person.name}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="task-modal-field">
              <label>Email</label>
              <input
                className="task-modal-input task-modal-readonly-input"
                type="email"
                value={editedTask.personEmail || editedTask.email || ''}
                readOnly
                placeholder="Email will auto-populate from requester"
              />
            </div>
          </div>
          
          {/* Created and Last Updated on same row */}
          {(task.created_at || task.updated_at) && (
            <div className="task-modal-row">
              {task.created_at && (
                <div className="task-modal-field">
                  <label>Created</label>
                  <p className="task-modal-readonly">{new Date(task.created_at).toLocaleDateString()}</p>
                </div>
              )}
              
              {task.updated_at && (
                <div className="task-modal-field">
                  <label>Last Updated</label>
                  <p className="task-modal-readonly">{new Date(task.updated_at).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assignees Section */}
        <div className="task-modal-assignees">
          <label className="task-modal-assignees-label">Assignees</label>
          {assignees.length > 0 && (
            <div className="task-modal-assignees-list">
              {assignees.map((assignee) => (
                <div key={assignee.id} className="task-modal-assignee-item">
                  {editingAssignee === assignee.id ? (
                    // Edit mode
                    <div className="task-modal-assignee-edit-form">
                      <span className="task-modal-assignee-name">{assignee.person_name}</span>
                      <div className="task-modal-assignee-edit-inputs">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          className="task-modal-input"
                          placeholder="Hours"
                          value={editAssigneeHours}
                          onChange={(e) => setEditAssigneeHours(e.target.value)}
                        />
                        <input
                          type="number"
                          step="1"
                          min="0"
                          className="task-modal-input"
                          placeholder="Points"
                          value={editAssigneePoints}
                          onChange={(e) => setEditAssigneePoints(e.target.value)}
                        />
                        <button
                          type="button"
                          className="task-modal-assignee-save"
                          onClick={() => handleUpdateAssignee(assignee.id)}
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          className="task-modal-assignee-cancel"
                          onClick={handleCancelEditAssignee}
                          title="Cancel"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <>
                      <div className="task-modal-assignee-info">
                        <span className="task-modal-assignee-name">{assignee.person_name}</span>
                        {(assignee.assigned_hours || assignee.assigned_points) && (
                          <span className="task-modal-assignee-allocation">
                            {assignee.assigned_hours && `${assignee.assigned_hours}h`}
                            {assignee.assigned_hours && assignee.assigned_points && ' / '}
                            {assignee.assigned_points && `${assignee.assigned_points}pts`}
                          </span>
                        )}
                      </div>
                      {canEdit && (
                        <div className="task-modal-assignee-actions">
                          <button
                            className="task-modal-assignee-edit"
                            onClick={() => handleEditAssignee(assignee)}
                            title="Edit assignee"
                          >
                            ✎
                          </button>
                          <button
                            className="task-modal-assignee-remove"
                            onClick={() => handleRemoveAssignee(assignee.id)}
                            title="Remove assignee"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <form className="task-modal-assignee-form" onSubmit={handleAddAssignee}>
              <select
                className="task-modal-select"
                value={newAssigneePersonId}
                onChange={(e) => setNewAssigneePersonId(e.target.value)}
                required
              >
                <option value="">Select person...</option>
                {people
                  .filter(p => !assignees.some(a => a.person_id === p.id))
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
              </select>
              <input
                type="number"
                step="0.5"
                min="0"
                className="task-modal-input"
                placeholder="Hours"
                value={newAssigneeHours}
                onChange={(e) => setNewAssigneeHours(e.target.value)}
              />
              <input
                type="number"
                step="1"
                min="0"
                className="task-modal-input"
                placeholder="Points"
                value={newAssigneePoints}
                onChange={(e) => setNewAssigneePoints(e.target.value)}
              />
              <button type="submit" className="task-modal-assignee-add-btn">Add</button>
            </form>
          )}
        </div>

        {/* Subtasks Section */}
        <div className="task-modal-subtasks">
          <label className="task-modal-subtasks-label">Subtasks</label>
          {subtasks.length > 0 && (
            <div className="task-modal-subtasks-list">
              {subtasks.map((subtask) => (
                <div key={subtask.id} className="task-modal-subtask-item">
                  {editingSubtask === subtask.id ? (
                    // Edit mode
                    <div className="task-modal-subtask-edit-form">
                      <input
                        type="text"
                        className="task-modal-input"
                        placeholder="Subtask title"
                        value={subtask.title}
                        onChange={(e) => {
                          const updated = subtasks.map(s => 
                            s.id === subtask.id ? { ...s, title: e.target.value } : s
                          );
                          setSubtasks(updated);
                        }}
                      />
                      <textarea
                        className="task-modal-textarea"
                        placeholder="Description (optional)"
                        value={subtask.description || ''}
                        onChange={(e) => {
                          const updated = subtasks.map(s => 
                            s.id === subtask.id ? { ...s, description: e.target.value } : s
                          );
                          setSubtasks(updated);
                        }}
                        rows="2"
                      />
                      <div className="task-modal-subtask-edit-fields">
                        <select
                          className="task-modal-select"
                          value={subtask.statusId || ''}
                          onChange={(e) => {
                            const updated = subtasks.map(s => 
                              s.id === subtask.id ? { ...s, statusId: e.target.value ? parseInt(e.target.value) : null } : s
                            );
                            setSubtasks(updated);
                          }}
                        >
                          <option value="">No Status</option>
                          {statuses.map((status) => (
                            <option key={status.id} value={status.id}>
                              {status.status}
                            </option>
                          ))}
                        </select>
                        <select
                          className="task-modal-select"
                          value={subtask.typeId || ''}
                          onChange={(e) => {
                            const updated = subtasks.map(s => 
                              s.id === subtask.id ? { ...s, typeId: e.target.value ? parseInt(e.target.value) : null } : s
                            );
                            setSubtasks(updated);
                          }}
                        >
                          <option value="">No Type</option>
                          {[...taskTypes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.type || type.type_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="task-modal-subtask-edit-actions">
                        <button
                          type="button"
                          className="task-modal-subtask-save"
                          onClick={() => {
                            const subtaskToUpdate = subtasks.find(s => s.id === subtask.id);
                            handleUpdateSubtask(subtask.id, {
                              title: subtaskToUpdate.title,
                              description: subtaskToUpdate.description || null,
                              statusId: subtaskToUpdate.statusId || null,
                              typeId: subtaskToUpdate.typeId || null
                            });
                          }}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="task-modal-subtask-cancel"
                          onClick={() => {
                            setEditingSubtask(null);
                            loadSubtasks(editedTask.id); // Reload to reset changes
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode - collapsible
                    <div className="task-modal-subtask-display">
                      {/* Collapsed Header - Always Visible */}
                      <div 
                        className="task-modal-subtask-header-collapsed"
                        onClick={() => {
                          const newExpanded = new Set(expandedSubtasks);
                          if (newExpanded.has(subtask.id)) {
                            newExpanded.delete(subtask.id);
                          } else {
                            newExpanded.add(subtask.id);
                          }
                          setExpandedSubtasks(newExpanded);
                        }}
                      >
                        <div className="task-modal-subtask-header-left">
                          <span className="task-modal-subtask-expand-icon">
                            {expandedSubtasks.has(subtask.id) ? '▼' : '▶'}
                          </span>
                          <input
                            type="text"
                            className="task-modal-input task-modal-subtask-title-input"
                            value={subtask.title}
                            onChange={(e) => handleSubtaskFieldUpdate(subtask.id, 'title', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!canEdit}
                            placeholder="Subtask title"
                          />
                          <select
                            className="task-modal-select task-modal-subtask-status-select"
                            value={subtask.statusId || ''}
                            onChange={(e) => handleSubtaskFieldUpdate(subtask.id, 'statusId', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!canEdit}
                          >
                            <option value="">No Status</option>
                            {statuses.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.status}
                              </option>
                            ))}
                          </select>
                        </div>
                        {canEdit && (
                          <button
                            className="task-modal-subtask-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubtask(subtask.id);
                            }}
                            title="Delete subtask"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {/* Expanded Content - Only visible when expanded */}
                      {expandedSubtasks.has(subtask.id) && (
                        <div className="task-modal-subtask-expanded-content">
                          {canEdit && (
                            <button
                              className="task-modal-subtask-edit-description"
                              onClick={() => setEditingSubtask(subtask.id)}
                              title="Edit description"
                            >
                              {subtask.description ? 'Edit Description' : 'Add Description'}
                            </button>
                          )}

                          {subtask.description && !editingSubtask && (
                            <p className="task-modal-subtask-description">{subtask.description}</p>
                          )}

                          {/* Subtask Assignees */}
                          <div className="task-modal-subtask-assignees-section">
                            {subtask.assignees && subtask.assignees.length > 0 && (
                              <div className="task-modal-subtask-assignees-list">
                                {subtask.assignees.map((assignee) => (
                                  <div key={assignee.id} className="task-modal-subtask-assignee-item">
                                    {editingSubtaskAssignee === `${subtask.id}-${assignee.id}` ? (
                                      // Edit mode
                                      <div className="task-modal-subtask-assignee-edit-form">
                                        <span className="task-modal-subtask-assignee-name">{assignee.person_name}</span>
                                        <div className="task-modal-subtask-assignee-edit-inputs">
                                          <input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            className="task-modal-input"
                                            placeholder="Hours"
                                            value={editSubtaskAssigneeHours}
                                            onChange={(e) => setEditSubtaskAssigneeHours(e.target.value)}
                                          />
                                          <input
                                            type="number"
                                            step="1"
                                            min="0"
                                            className="task-modal-input"
                                            placeholder="Points"
                                            value={editSubtaskAssigneePoints}
                                            onChange={(e) => setEditSubtaskAssigneePoints(e.target.value)}
                                          />
                                          <button
                                            type="button"
                                            className="task-modal-subtask-assignee-save"
                                            onClick={() => handleUpdateSubtaskAssignee(subtask.id, assignee.id)}
                                            title="Save"
                                          >
                                            ✓
                                          </button>
                                          <button
                                            type="button"
                                            className="task-modal-subtask-assignee-cancel"
                                            onClick={handleCancelEditSubtaskAssignee}
                                            title="Cancel"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      // Display mode
                                      <>
                                        <div className="task-modal-subtask-assignee-info">
                                          <span className="task-modal-subtask-assignee-name">{assignee.person_name}</span>
                                          {(assignee.assigned_hours || assignee.assigned_points) && (
                                            <span className="task-modal-subtask-assignee-allocation">
                                              {assignee.assigned_hours && `${assignee.assigned_hours}h`}
                                              {assignee.assigned_hours && assignee.assigned_points && ' / '}
                                              {assignee.assigned_points && `${assignee.assigned_points}pts`}
                                            </span>
                                          )}
                                        </div>
                                        {canEdit && (
                                          <div className="task-modal-subtask-assignee-actions">
                                            <button
                                              className="task-modal-subtask-assignee-edit"
                                              onClick={() => handleEditSubtaskAssignee(subtask.id, assignee)}
                                              title="Edit assignee"
                                            >
                                              ✎
                                            </button>
                                            <button
                                              className="task-modal-subtask-assignee-remove"
                                              onClick={() => handleRemoveSubtaskAssignee(subtask.id, assignee.id)}
                                              title="Remove assignee"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {canEdit && (
                              <form className="task-modal-subtask-assignee-form" onSubmit={(e) => handleAddSubtaskAssignee(e, subtask.id)}>
                                <div className="task-modal-subtask-assignee-form-row">
                                  <select
                                    className="task-modal-select task-modal-subtask-type-select"
                                    value={subtask.typeId || ''}
                                    onChange={(e) => handleSubtaskFieldUpdate(subtask.id, 'typeId', e.target.value)}
                                    disabled={!canEdit}
                                  >
                                    <option value="">No Type</option>
                                    {[...taskTypes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((type) => (
                                      <option key={type.id} value={type.id}>
                                        {type.type || type.type_name}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    className="task-modal-select"
                                    value={newSubtaskAssigneePersonId[subtask.id] || ''}
                                    onChange={(e) => setNewSubtaskAssigneePersonId(prev => ({ ...prev, [subtask.id]: e.target.value }))}
                                    required
                                  >
                                    <option value="">Select person...</option>
                                    {people
                                      .filter(p => !subtask.assignees || !subtask.assignees.some(a => a.person_id === p.id))
                                      .map((person) => (
                                        <option key={person.id} value={person.id}>
                                          {person.name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                                <div className="task-modal-subtask-assignee-form-row">
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    className="task-modal-input"
                                    placeholder="Hours"
                                    value={newSubtaskAssigneeHours[subtask.id] || ''}
                                    onChange={(e) => setNewSubtaskAssigneeHours(prev => ({ ...prev, [subtask.id]: e.target.value }))}
                                  />
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    className="task-modal-input"
                                    placeholder="Points"
                                    value={newSubtaskAssigneePoints[subtask.id] || ''}
                                    onChange={(e) => setNewSubtaskAssigneePoints(prev => ({ ...prev, [subtask.id]: e.target.value }))}
                                  />
                                  <button type="submit" className="task-modal-subtask-assignee-add-btn">Add</button>
                                </div>
                              </form>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <>
              {!showNewSubtaskForm ? (
                <button
                  type="button"
                  className="task-modal-subtask-add-btn"
                  onClick={() => setShowNewSubtaskForm(true)}
                >
                  + Add Subtask
                </button>
              ) : (
                <form className="task-modal-subtask-form" onSubmit={(e) => {
                  handleCreateSubtask(e);
                  setShowNewSubtaskForm(false);
                }}>
                  <input
                    type="text"
                    className="task-modal-input"
                    placeholder="Subtask title *"
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    required
                  />
                  <textarea
                    className="task-modal-textarea"
                    placeholder="Description (optional)"
                    value={newSubtaskDescription}
                    onChange={(e) => setNewSubtaskDescription(e.target.value)}
                    rows="2"
                  />
                  <div className="task-modal-subtask-form-fields">
                    <select
                      className="task-modal-select"
                      value={newSubtaskStatusId}
                      onChange={(e) => setNewSubtaskStatusId(e.target.value)}
                    >
                      <option value="">No Status</option>
                      {statuses.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.status}
                        </option>
                      ))}
                    </select>
                    <select
                      className="task-modal-select"
                      value={newSubtaskTypeId}
                      onChange={(e) => setNewSubtaskTypeId(e.target.value)}
                    >
                      <option value="">No Type</option>
                      {[...taskTypes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.type || type.type_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="task-modal-subtask-form-actions">
                    <button type="submit" className="task-modal-subtask-add-btn">Add</button>
                    <button
                      type="button"
                      className="task-modal-subtask-cancel"
                      onClick={() => {
                        setShowNewSubtaskForm(false);
                        setNewSubtaskTitle('');
                        setNewSubtaskDescription('');
                        setNewSubtaskStatusId('');
                        setNewSubtaskTypeId('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>

        {/* Comments Section */}
        <div className="task-modal-comments">
          <label className="task-modal-comments-label">Comments</label>
          <div className="task-modal-comments-list">
            {comments.length === 0 ? (
              <p className="task-modal-no-comments">No comments yet</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="task-modal-comment">
                  <div className="task-modal-comment-header">
                    <span className="task-modal-comment-author">{comment.personName || comment.author || 'Unknown'}</span>
                    <span className="task-modal-comment-time">
                      {new Date(comment.created_at || comment.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div 
                    className="task-modal-comment-text" 
                    dangerouslySetInnerHTML={{ __html: comment.text || comment.comment_text || '' }}
                  />
                </div>
              ))
            )}
          </div>
          {canEdit && (
          <div className="task-modal-comment-input">
            <RichTextEditor
              value={newComment}
              onChange={setNewComment}
              placeholder="Add a comment..."
              className="comment-editor"
              onFileAttach={handleFileUpload}
              taskId={editedTask?.id}
            />
            <button 
              className="task-modal-comment-btn" 
              onClick={handleAddComment}
              disabled={!newComment || !newComment.replace(/<[^>]*>/g, '').trim()}
            >
              Add Comment
            </button>
          </div>
          )}
        </div>

        {canEdit && (
        <div className="task-modal-footer">
          <button className="task-modal-delete-btn" onClick={handleDelete}>
            Delete Task
          </button>
        </div>
        )}
        </div>
        </div>
      </div>
  );

  // Render modal directly to document.body using portal to avoid layout issues
  return (
    <>
      {ReactDOM.createPortal(modalContent, document.body)}
      {/* File Preview Modal - rendered separately to document.body for full screen */}
      {previewFile && (isImageFile(previewFile.mime_type) || isVideoFile(previewFile.mime_type, previewFile.file_name)) && ReactDOM.createPortal(
        <div className="task-modal-image-preview-overlay" onClick={() => setPreviewFile(null)}>
          <div className="task-modal-image-preview-content" onClick={(e) => e.stopPropagation()}>
            {/* File name at top */}
            <div className="task-modal-image-preview-header">
              <span className="task-modal-image-preview-filename">{previewFile.original_file_name || previewFile.file_name}</span>
            </div>
            
            {/* Action buttons */}
            <div className="task-modal-image-preview-actions">
              <button 
                className="task-modal-image-preview-download" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Use unique filename for blob storage download, but original filename for download attribute
                  const blobFileName = previewFile.file_name || previewFile.image_url.split('/').pop();
                  const downloadFileName = previewFile.original_file_name || previewFile.file_name || blobFileName;
                  // Use backend proxy to avoid CORS issues
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                  const baseUrl = apiUrl.match(/^https?:\/\//) ? apiUrl : `https://${apiUrl}`;
                  const downloadUrl = `${baseUrl}/images/download/${encodeURIComponent(blobFileName)}`;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = downloadFileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                title="Download image"
              >
                ⬇️
              </button>
              <button 
                className="task-modal-image-preview-delete" 
                onClick={async (e) => {
                  e.stopPropagation();
                  const displayName = previewFile.original_file_name || previewFile.file_name;
                  if (window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
                    await handleDeleteFile(previewFile.id, previewFile.file_name);
                    setPreviewFile(null);
                  }
                }}
                title="Delete file"
              >
                🗑️
              </button>
              <button className="task-modal-image-preview-close" onClick={() => setPreviewFile(null)} title="Close (ESC)">×</button>
            </div>
            
            {/* Image or Video */}
            {isImageFile(previewFile.mime_type) ? (
              <img src={previewFile.image_url} alt={previewFile.file_name} className="task-modal-image-preview-img" />
            ) : isVideoFile(previewFile.mime_type, previewFile.file_name) ? (
              <video 
                src={previewFile.image_url} 
                controls 
                className="task-modal-image-preview-img task-modal-video-preview-player"
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

